<script>
import {difference} from 'lodash'
import ActionTemplates from './ActionTemplates'
import ActionSelection from './ActionSelection'
import Workflow from './Workflow'
import ModalCard from './ModalCard'
import JSONEditor from './JSONEditor'
import ActionCollection from './ActionCollection'
import ActionForm from './ActionForm'
import CopyToClipboard from './CopyToClipboard'
import LZString from 'lz-string'

export default {

  components: {
    ActionSelection,
    ModalCard,
    JSONEditor,
    ActionCollection,
    ActionForm,
    CopyToClipboard
  },

  props: {
    workflow: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      showActionSelection: false,
      actionSelectionComponent: null,
      depth: 0,

      showJSONView: false,
      actionToEdit: null
    }
  },

  methods: {

    saveWorkflow() {
      this.workflow.save()
      this.$emit('save')
    },

    destroyAction(actionToRemove) {
      this.workflow.actions = this.workflow.actions.filter((filter)=>{
        return actionToRemove.uuid != filter.uuid
      })
    },

    editAction(action) {
      this.actionToEdit = action
    }

  },

  mounted() {
  },

  watch: {
    actionToEdit() {
      if (this.actionToEdit) {
        this.showActionSelection = true
      }
    },

    showActionSelection: {
      immediate: true,
      handler() {
        this.depth = this.showActionSelection ? 1 : 0

        if (!this.showActionSelection) {
          this.actionToEdit = null
        }

        this.$nextTick(()=>{
          this.actionSelectionComponent = this.$refs.actionSelection
        })
      }
    },

    depth() {
      if (this.depth > 0) { return }
      this.showActionSelection = false
    }
  },

  computed: {

    workflowUrl() {
      let url = location.href.replace(location.hash, '')
      let json = Object.assign({}, this.workflowAsJSON)
      let data = LZString.compressToEncodedURIComponent(JSON.stringify({workflows: [json]}))
      return `${url}#workflow:${data}`
    },

    workflowAsJSON: {
      get() {
        return this.workflow.asJson()
      },
      set(data) {
        this.workflow._data = data
      }
    },

    isPersisted() {
      return this.workflow && this.workflow.uuid
    },

    title() {
      if (this.isPersisted) {
        return `Edit Workflow "${this.workflow.name}"`
      } else {
        return "New Workflow"
      }
    },

    allTemplates() {
      return ActionTemplates.storage.allGrouped()
    },

    hasActionTemplates() {
      return this.workflow.actions.length > 0
    },

    isInvalid() {
      return !this.hasActionTemplates
    }

  }

}
</script>

<template>

<modal-card :title="title" v-if="workflow" :depth.sync="depth">
  <template slot="header-right">

    <copy-to-clipboard v-if="isPersisted" title="Copy" buttonType="is-text" :content="workflowUrl"></copy-to-clipboard>

  </template>

  <template slot="body">

    <div v-if="showJSONView">
      <JSONEditor @cancel="showJSONView = false" v-model="workflowAsJSON"></JSONEditor>
    </div>

    <div v-else :class="{detailVisible: showActionSelection}">
    <transition name="modal">

      <section v-if="showActionSelection" key="section-1">

        <action-form v-if="actionToEdit"
          :template="actionToEdit.template()"
          :action.sync="actionToEdit"
          :workflow="workflow"
        ></action-form>

        <action-selection v-else ref="actionSelection" v-model="workflow.actions" :workflow="workflow" @close="showActionSelection = false"></action-selection>
      </section>

      <section v-else key="section-2">
        <b-field horizontal label="Name">
          <b-input placeholder="Choose a name" name="name" v-model="workflow.name"></b-input>
        </b-field>

        <b-field horizontal label="Tags">
          <b-taginput v-model="workflow.tags" icon="label" placeholder="Add a tag"></b-taginput>
        </b-field>

        <b-field horizontal message="You can choose outputs from every action that should be stored as a global output for this workflow. These can then be used as input in other workflows">
          <template slot="label">
            <label>Global output</label> <br>
            <b-icon icon="earth" size="is-small"></b-icon>
          </template>
          <div>
            <div v-if="workflow.output" v-for="(outputSetting, id) in workflow.output" :key="id">
              {{ outputSetting.action.name }}: <span class="tag">{{ outputSetting.name }}</span>
            </div>
          </div>
        </b-field>

        <b-field horizontal label="ActionTemplates">
          <template slot="label">
            Actions
          </template>
          <div v-if="workflow.actions.length > 0">
            <action-collection :actions.sync="workflow.actions" :globalOutput.sync="workflow.output" @destroyAction="destroyAction" @editAction="editAction"></action-collection>
          </div>
          <div v-else>
            <span class="has-text-grey">No actions so far</span>
          </div>
        </b-field>

        <b-field horizontal>
          <p class="control">
            <b-button @click="showActionSelection = true" icon-left="playlist-plus">Add Action</b-button>
          </p>
        </b-field>

      </section>
    </transition>
    </div>

  </template>

  <template slot="footer">
    <div v-if="!showActionSelection">
      <b-button :type="{'is-primary': showJSONView}" icon-left="code-braces" @click="showJSONView = !showJSONView">Toggle JSON View</b-button>
    </div>

    <div v-if="actionSelectionComponent">
      <div class="buttons">

        <b-button class="is-text" @click="actionSelectionComponent.$emit('close')">Back</b-button>
        <b-button class="is-primary"
              :disabled="!actionSelectionComponent.selectedActionTemplate"
              @click="actionSelectionComponent.addAction"
        >Add this action</b-button>
      </div>

    </div>
    <div class="buttons" v-if="actionToEdit">
      <b-button class="is-primary" @click="showActionSelection = false" >Back</b-button>
    </div>

    <div v-else>
      <div class="buttons">
        <b-button type="is-text" @click="$emit('cancel')">Cancel</b-button>
        <b-button type="is-primary" :disabled="isInvalid || showJSONView || actionSelectionComponent" @click="saveWorkflow">
          Save Workflow
        </b-button>
      </div>
    </div>

  </template>

</modal-card>
</template>

<style lang="scss">
.small-space-bottom {
  margin-bottom: 3px;
}
.horizontal-line-height {
  line-height: 34px;
}

.modal-leave-active {
  display: none;
}
.modal-enter-active, .modal-leave-active {
  transition: all .2s ease-out;
}
.modal-enter, .modal-leave-to{
  transform: translateX(-300px);
}
.detailVisible {
  .modal-enter, .modal-leave-to{
    transform: translateX(300px);
  }
}

</style>